/**
 * syllabusApi — 강의 v2(검색 프리셋 나무)의 서버 호출.
 *
 * 계약: `docs/learning-composition-v2/BE-20260911-reply-syllabus-preset.md` §4·§5
 *       (dev `20cb46d`, 운영 미반영 — 배포 전 서버는 강의 경로가 404, 조건식 검색이 405).
 *
 * 공용 요청 함수(fetchWithTokenRefresh)를 쓰지 않는 이유: 그쪽은 400·403 을 고정 문구로 갈아 끼우고
 * 상태 코드와 서버의 사유(data.reason)를 버린다. 이 기능은 사유마다 화면이 다르게 말해야 한다 —
 * 공유받은 강의를 고치려 함(read_only) · 다른 곳에서 먼저 고침(stale) · 모순 식(contradiction, 어느 묶음·조건인지) 등.
 * 그래서 문제 API(problemApi)의 상태 코드를 살리는 요청 함수와 같은 모양으로 둔다.
 *
 * 쓰기는 모두 강의의 revision 을 1 올린다(회신 §4). 화면은 응답의 revision 을 다음 요청의 ifRevision 으로 보낸다 —
 * 두 창(관리 페이지·다른 탭)에서 같은 강의를 고치면 늦게 저장한 쪽이 409 stale 을 받는다.
 */
import { fetchAPI } from '../../utils/apiUtils';

const BASE = '/api/v2/syllabi';
const NETWORK_MSG = '서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.';

/**
 * 요청 한 번. 성공이면 봉투의 data, 실패면 Error 에 { httpStatus, reason, data, envelope } 를 실어 던진다.
 * envelope — 응답이 표준 봉투({status,…})였는가. 배포 전 서버는 경로가 없어 봉투 없는 404·405 를 준다.
 */
async function call(path, { method = 'GET', body, signal = null } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    const err = new Error(fallbackMsg);
    err.httpStatus = 401;
    throw err;
  }
  let response;
  try {
    response = await fetchAPI(path, {
      method,
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (netErr) {
    // 취소는 네트워크 오류가 아니다 — 부른 쪽이 이미 버린 요청이라 그대로 넘겨 조용히 끝나게 한다
    if (netErr?.name === 'AbortError') throw netErr;
    throw new Error(NETWORK_MSG);
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남기면 다음 화면이 또 401 을 맞는다
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    const err = new Error(fallbackMsg);
    err.httpStatus = 401;
    throw err;
  }
  let payload = null;
  try { payload = await response.json(); } catch (_) { /* 아래에서 상태 코드로 표면화 */ }
  const envelope = payload != null && typeof payload.status === 'string';
  if (!response.ok || !envelope || payload.status !== 'success') {
    const err = new Error((envelope && payload.message) || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.envelope = envelope;
    err.data = envelope ? (payload.data ?? null) : null;
    err.reason = err.data?.reason ?? null;
    throw err;
  }
  return payload.data;
}

const qs = (params) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    const s = String(v).trim();
    if (s !== '') p.append(k, s);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const pageOf = (d, page, perPage) => {
  const items = Array.isArray(d?.items) ? d.items : [];
  return {
    items,
    page: d?.page ?? page,
    perPage: d?.per_page ?? perPage,
    totalItems: Number.isInteger(d?.total_items) ? d.total_items : items.length,
    totalPages: Number.isInteger(d?.total_pages) ? d.total_pages : 1,
  };
};

// ── 강의(헤더) ──

/** P1 목록 — scope: all(내 것+공유받은 것) · mine · shared. 매니저도 남의 개인 강의는 안 보인다(D3) */
export async function listSyllabi({ scope = 'all', q = '', page = 1, perPage = 100 } = {}, getAccessToken) {
  const d = await call(`${BASE}${qs({ scope, q, page, per_page: perPage })}`, {}, getAccessToken, '강의 목록을 불러오지 못했습니다.');
  return { ...pageOf(d, page, perPage), scope: d?.scope ?? scope };
}

/** P2 새 강의 — 이름 1~120자. 공유 없음·revision 1 로 선다 */
export function createSyllabus({ name, description } = {}, getAccessToken) {
  const body = { name };
  if (description !== undefined) body.description = description;
  return call(BASE, { method: 'POST', body }, getAccessToken, '강의를 만들지 못했습니다.');
}

/** P3 상세 — 헤더 + 줄 평면 목록(부모 다음 자식, 형제는 order_no 순) */
export function getSyllabus(syllabusId, getAccessToken) {
  return call(`${BASE}/${syllabusId}`, {}, getAccessToken, '강의를 불러오지 못했습니다.');
}

/** P4 헤더 고치기 — 준 키만 보낸다 */
export function updateSyllabus(syllabusId, { name, description, ifRevision } = {}, getAccessToken) {
  const body = {};
  if (name !== undefined) body.name = name;
  if (description !== undefined) body.description = description;
  if (ifRevision != null) body.if_revision = ifRevision;
  return call(`${BASE}/${syllabusId}`, { method: 'PATCH', body }, getAccessToken, '강의를 고치지 못했습니다.');
}

/** P5 지우기 — 줄까지 함께. 주인만 */
export function deleteSyllabus(syllabusId, getAccessToken) {
  return call(`${BASE}/${syllabusId}`, { method: 'DELETE' }, getAccessToken, '강의를 지우지 못했습니다.');
}

/** P6 사본 — 줄(조건식 포함)까지 복제, 주인은 누른 사람. 이름을 안 주면 원본 이름 그대로 */
export function copySyllabus(syllabusId, { name } = {}, getAccessToken) {
  const body = {};
  if (name !== undefined) body.name = name;
  return call(`${BASE}/${syllabusId}/copy`, { method: 'POST', body }, getAccessToken, '사본을 만들지 못했습니다.');
}

/** P7 공유 — null(개인) · 'managers'(매니저·관리자 전원) · 'all'(스태프 전원) */
export function shareSyllabus(syllabusId, shareScope, { ifRevision } = {}, getAccessToken) {
  const body = { share_scope: shareScope ?? null };
  if (ifRevision != null) body.if_revision = ifRevision;
  return call(`${BASE}/${syllabusId}/share`, { method: 'PUT', body }, getAccessToken, '공유 설정을 바꾸지 못했습니다.');
}

// ── 줄 ──

/** N1 줄 추가 — parentId null 이면 최상위, orderNo 를 안 주면 형제 끝. query 는 서버가 정규화해 저장한다 */
export function addNode(syllabusId, { parentId = null, orderNo, name, query, ifRevision } = {}, getAccessToken) {
  const body = { parent_id: parentId ?? null, name };
  if (orderNo != null) body.order_no = orderNo;
  if (query !== undefined) body.query = query;
  if (ifRevision != null) body.if_revision = ifRevision;
  return call(`${BASE}/${syllabusId}/nodes`, { method: 'POST', body }, getAccessToken, '줄을 추가하지 못했습니다.');
}

/**
 * N2 줄 고치기 — **준 키만** 보낸다. parentId: null 은 「최상위로 옮김」, query: null 은 「조건 없는 제목 줄로」라
 * 값이 null 인 것과 키가 없는 것의 뜻이 다르다(그래서 `in` 으로 가른다).
 */
export function updateNode(syllabusId, nodeId, patch = {}, getAccessToken) {
  const body = {};
  if ('name' in patch) body.name = patch.name;
  if ('parentId' in patch) body.parent_id = patch.parentId;
  if ('query' in patch) body.query = patch.query;
  if (patch.ifRevision != null) body.if_revision = patch.ifRevision;
  return call(`${BASE}/${syllabusId}/nodes/${nodeId}`, { method: 'PATCH', body }, getAccessToken, '줄을 고치지 못했습니다.');
}

/** N3 줄 지우기 — 하위 줄까지 함께 */
export function deleteNode(syllabusId, nodeId, { ifRevision } = {}, getAccessToken) {
  const opts = { method: 'DELETE' };
  if (ifRevision != null) opts.body = { if_revision: ifRevision };
  return call(`${BASE}/${syllabusId}/nodes/${nodeId}`, opts, getAccessToken, '줄을 지우지 못했습니다.');
}

/** N4 형제 순서 — 그 부모의 자식 전부를 원하는 순서로(하나라도 빠지면 400 order_mismatch) */
export function reorderNodes(syllabusId, parentId, nodeIds, { ifRevision } = {}, getAccessToken) {
  const body = { parent_id: parentId ?? null, node_ids: nodeIds };
  if (ifRevision != null) body.if_revision = ifRevision;
  return call(`${BASE}/${syllabusId}/nodes/order`, { method: 'PUT', body }, getAccessToken, '줄 순서를 바꾸지 못했습니다.');
}

// ── 검색 ──

/**
 * S1 조건식 검색 — POST /api/v2/exam-papers/problem-search(주소줄 검색과 같은 주소, 본문으로).
 * filters 는 주소줄 파라미터와 같은 키·뜻(문자열)이고 식과 「그리고」로 겹친다(시험지 조립 창의 폼 조건).
 * 응답은 주소줄 검색과 같은 봉투 + warnings(없는 개념·라벨 — 없으면 []). 5초를 넘으면 408.
 * signal — 늦은 검색을 취소한다(설계 FE-20260913 §5). 취소는 화면이 버리는 것이고 서버는 끝까지 계산할 수 있다.
 */
export async function searchByExpr({
  expr = null, filters = null, page = 1, perPage = 20, withBody = false, signal = null,
} = {}, getAccessToken) {
  const body = { expr, page, per_page: perPage };
  if (filters && Object.keys(filters).length) body.filters = filters;
  if (withBody) body.with_body = 1;
  const d = await call('/api/v2/exam-papers/problem-search', { method: 'POST', body, signal }, getAccessToken, '문제를 불러오지 못했습니다.');
  return { ...pageOf(d, page, perPage), warnings: Array.isArray(d?.warnings) ? d.warnings : [] };
}

// ── 학생 × 강의 행 × 난이도 집계 ──

/**
 * 학습 화면 「강의」 보기의 난이도 배지 — GET /api/v2/syllabi/{id}/progress?student_id=&node_ids=
 * 계약: docs/learning-composition-v2/BE-20260914-reply-student-syllabus-progress.md (dev 568d63e).
 * 조건 있는 행마다 「행 식에 맞는 문제 중 이 학생이 푼 것」을 난이도 "1"~"5"·"none" 별 {correct, wrong, unanswered} 로 —
 * latest(문제별 최근 1건)·attempts(기록마다) 둘 다. nodes·skipped 는 늘 배열로 돌려준다.
 * 403 은 둘로 갈린다 — out_of_scope(강의를 볼 수 없음) · student_out_of_scope(담당 학생이 아님). 요청 전체 30초를 넘으면 408.
 */
export async function getSyllabusProgress(syllabusId, { studentId, nodeIds = null, signal = null } = {}, getAccessToken) {
  const nodes = Array.isArray(nodeIds) && nodeIds.length ? nodeIds.join(',') : null;
  const d = await call(`${BASE}/${syllabusId}/progress${qs({ student_id: studentId, node_ids: nodes })}`, { signal }, getAccessToken, '강의 배지를 불러오지 못했습니다.');
  return { ...d, nodes: Array.isArray(d?.nodes) ? d.nodes : [], skipped: Array.isArray(d?.skipped) ? d.skipped : [] };
}

/** 라벨 사전 — GET /api/v2/labels?q=(이름 부분 검색). 항목: label_uuid · label_name · label_type · problem_count */
export async function listLabels({ q = '', perPage = 20 } = {}, getAccessToken) {
  const d = await call(`/api/v2/labels${qs({ q, per_page: perPage })}`, {}, getAccessToken, '라벨 목록을 불러오지 못했습니다.');
  if (Array.isArray(d)) return d;
  return Array.isArray(d?.items) ? d.items : [];
}

// ── 오류 문구 ──

const REASON_TEXT = {
  out_of_scope: '볼 수 있는 범위 밖의 강의입니다.',
  student_out_of_scope: '담당 학생이 아니어서 볼 수 없습니다.',
  read_only: '공유받은 강의는 고칠 수 없습니다. 사본을 만들어 고치세요.',
  stale: '다른 곳에서 먼저 고쳤습니다. 새로 불러온 뒤 다시 해 주세요.',
  max_depth: '줄은 다섯 단까지만 만들 수 있습니다.',
  max_nodes: '강의 한 개의 줄은 500개까지입니다.',
  cycle: '자기 자신이나 자기 하위 줄 밑으로는 옮길 수 없습니다.',
  order_mismatch: '줄 순서가 서버와 어긋났습니다. 새로 불러온 뒤 다시 해 주세요.',
  search_timeout: '검색이 5초를 넘어 멈췄습니다. 조건을 좁혀 주세요.',
};

/** 배포 전 서버인가 — 경로가 없어 봉투 없는 404(강의 경로)·405(POST 검색)가 온다 */
export const isPredeployError = (err) => err != null && err.envelope === false && (err.httpStatus === 404 || err.httpStatus === 405);

/**
 * 오류를 사람의 말로. 식 오류(invalid_expr·contradiction·unknown_values·expr_too_large)는 서버 문구가 이미
 * 한국어이고 어느 묶음·조건인지까지 말하므로 그대로 쓴다.
 */
export function describeSyllabusError(err) {
  if (!err) return '';
  if (isPredeployError(err)) {
    return err.httpStatus === 405
      ? '서버에 아직 조건식 검색이 배포되지 않았습니다. 배포 뒤 다시 시도해 주세요.'
      : '서버에 아직 강의 기능이 배포되지 않았습니다. 배포 뒤 다시 열어 주세요.';
  }
  if (err.reason && REASON_TEXT[err.reason]) return REASON_TEXT[err.reason];
  if (err.httpStatus === 408) return REASON_TEXT.search_timeout;
  if (err.httpStatus === 404) return '이미 지워졌거나 없는 강의·줄입니다.';
  return err.message || '요청을 처리하지 못했습니다.';
}
